import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import {
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { CvsFeeScheduleType } from '../../types';
import { formatCurrency } from '../../utils/currency-helpers';
import {
  chargeToDisplayValue,
  daysSupplyToDisplayValue,
} from './cvs-fee-schedule-form/validation-schema';

interface UseCvsFeeScheduleColumnsProps {
  onEdit: (row: CvsFeeScheduleType) => void;
  onDelete: (row: CvsFeeScheduleType) => void;
  canWrite: boolean;
}

export const useCvsFeeScheduleColumns = ({
  onEdit,
  onDelete,
  canWrite,
}: UseCvsFeeScheduleColumnsProps): GridColDef[] =>
  useMemo(
    () => [
      {
        field: 'upc',
        headerName: 'UPC',
        width: 150,
        valueFormatter: (params): string => params.value ?? '--',
      },
      {
        field: 'charge',
        headerName: 'AWP',
        width: 120,
        renderCell: (
          params: GridRenderCellParams<CvsFeeScheduleType>,
        ): string => {
          const rawCharge = params.row.charge;
          if (!rawCharge) return '--';

          const dollars = chargeToDisplayValue(rawCharge);
          return isNaN(dollars) ? '--' : formatCurrency(dollars, 'USD', true);
        },
      },
      {
        field: 'price',
        headerName: 'CVS Price',
        width: 120,
        renderCell: (
          params: GridRenderCellParams<CvsFeeScheduleType>,
        ): string =>
          isNaN(params.row.price)
            ? '--'
            : formatCurrency(params.row.price, 'USD', true),
      },
      {
        field: 'revShare',
        headerName: 'Rev Share',
        width: 120,
        sortable: false,
        renderCell: (
          params: GridRenderCellParams<CvsFeeScheduleType>,
        ): string => {
          if (!params.row.charge) return '--';
          if (isNaN(params.row.price)) return '--';
          const awp = chargeToDisplayValue(params.row.charge);
          if (isNaN(awp)) return '--';

          return formatCurrency(awp - params.row.price, 'USD', true);
        },
      },
      {
        field: 'daysSupply',
        headerName: 'Days Supply',
        width: 110,
        valueFormatter: (params): string =>
          params.value ? daysSupplyToDisplayValue(params.value) : '--',
      },
      {
        field: 'paymentType',
        headerName: 'Payment Type',
        width: 140,
        valueFormatter: (params): string => params.value ?? '--',
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1,
        minWidth: 200,
      },
      ...(canWrite
        ? [
            {
              field: 'actions',
              headerName: 'Actions',
              type: 'actions',
              width: 100,
              getActions: (
                params: GridRowParams<CvsFeeScheduleType>,
              ): JSX.Element[] => [
                <GridActionsCellItem
                  key={`edit-${params.id}`}
                  icon={<EditIcon />}
                  label="Edit"
                  onClick={(): void => onEdit(params.row)}
                />,
                <GridActionsCellItem
                  key={`delete-${params.id}`}
                  icon={<DeleteOutlineIcon />}
                  label="Delete"
                  onClick={(): void => onDelete(params.row)}
                />,
              ],
            },
          ]
        : []),
    ],
    [onEdit, onDelete, canWrite],
  );
